/**
 * Resolve the best-available offer/acceptance rate for a uni or degree,
 * following the precedence ladder shipped with the offer_stats pipeline
 * (Feb 2026):
 *
 *   1. Course-level UCAS End-of-Cycle  (d.hesa_stats.ucas_eoc.offer_rate)
 *   2. Course-level AI Fetch           (d.admissions.offer_rate)
 *   3. Uni-level offer_stats           (uni.offer_stats.offer_rate)
 *   4. Legacy editorial estimate       (uni.acceptance_rate)
 *
 * The last tier is a stub for ~80 UK unis that share the seed default
 * 0.60, so we surface it with `source: 'legacy_editorial'` and the UI
 * renders a small amber "Editorial estimate" chip.
 *
 * Returns `null` if literally no number exists.
 *
 * Shape: { value: 0..1, source: string, confidence: string, label: string,
 *          evidence: { source_url?, cycle_year?, extracted_at? } }
 */

// Legacy defaults from seed_universities_uk_extended.py + friends. When
// the value is one of these AND no better source overrides, we render
// an amber "Editorial estimate" chip so users don't confuse a stub
// with a real, verified number.
const LEGACY_STUB_VALUES = new Set([0.60, 0.65, 0.9, 0.90, 0.59, 0.79, 0.82, 0.83, 0.86, 0.87, 0.88, 0.91, 0.92, 0.93]);

function _isStub(v) {
  if (v == null || typeof v !== "number") return false;
  // Match to 2 dp — accounts for tiny float noise.
  return LEGACY_STUB_VALUES.has(Math.round(v * 100) / 100);
}

export function resolveOfferRate({ degree, uni } = {}) {
  // Tier 1: course-level UCAS End-of-Cycle (gold standard)
  const eoc = degree?.hesa_stats?.ucas_eoc;
  if (eoc && eoc._status === "INGESTED" && typeof eoc.offer_rate === "number") {
    return {
      value: eoc.offer_rate,
      source: "ucas_eoc",
      metric_kind: "offer",
      confidence: "verified",
      label: "UCAS End-of-Cycle · course-level",
      evidence: {
        source_url: eoc.evidence?.url,
        cycle_year: eoc.evidence?.academic_year,
      },
    };
  }

  // Tier 2: course-level AI Fetch extraction (from Phase 3 daemon)
  const rateCourse = degree?.admissions?.offer_rate;
  if (typeof rateCourse === "number") {
    return {
      value: rateCourse,
      source: "course_ai_fetch",
      metric_kind: "offer",
      confidence: degree?.content_verified ? "verified" : "medium",
      label: "Course admissions page",
      evidence: {
        source_url:
          degree?.record_evidence?.url || degree?.record_evidence?.source_url,
      },
    };
  }

  // Tier 3: uni-level offer_stats (from wua-offer-rate-extract daemon
  // or the UCAS-EOC-provider ingest).
  const os = uni?.offer_stats;
  if (os && typeof os.offer_rate === "number") {
    // Feb 2026: the UCAS EOC provider bundle only lets us derive an
    // ACCEPTANCE rate per provider (offers-per-provider aren't in the
    // free bundle). We store the number in `offer_rate` for schema
    // continuity, but flag `source === "ucas-eoc-acceptance-rate"` so
    // the frontend can label it accurately.
    const isUcasEocAcceptance = os.source === "ucas-eoc-acceptance-rate";
    if (isUcasEocAcceptance) {
      return {
        value: os.offer_rate,
        source: "ucas_eoc_acceptance",
        metric_kind: "acceptance",
        confidence: os.confidence || "medium",
        label: `Acceptance rate · UCAS End-of-Cycle ${os.cycle_year || "2024"}`,
        evidence: {
          source_url: os.source_url || uni?.offer_rate_evidence?.source_url,
          cycle_year: os.cycle_year,
          note: os.note,
        },
      };
    }
    return {
      value: os.offer_rate,
      source: "uni_offer_stats",
      metric_kind: "offer",
      confidence: os.confidence || "medium",
      label: os.level === "undergraduate"
        ? `University-wide undergraduate · ${os.cycle_year || "recent cycle"}`
        : `University-wide · ${os.cycle_year || "recent cycle"}`,
      evidence: {
        source_url: uni?.offer_rate_evidence?.source_url,
        extracted_at: uni?.offer_rate_extracted_at,
        cycle_year: os.cycle_year,
      },
    };
  }

  // Tier 4: legacy `acceptance_rate` — mark clearly if it's a stub default
  if (typeof uni?.acceptance_rate === "number") {
    const stub = _isStub(uni.acceptance_rate);
    return {
      value: uni.acceptance_rate,
      source: stub ? "legacy_editorial_stub" : "legacy_editorial",
      metric_kind: "acceptance",
      confidence: "low",
      label: stub
        ? "Editorial estimate (seed default — awaiting verified data)"
        : "Editorial estimate",
      evidence: {},
    };
  }

  return null;
}

/** Human-readable metric name for the rate (used in tile headers). */
export function offerRateMetricLabel(or) {
  if (!or) return "Offer rate";
  return or.metric_kind === "acceptance" ? "Acceptance rate" : "Offer rate";
}

/**
 * Convenience: return a Tailwind chip classname + short label matching
 * the source, so every render site styles the confidence consistently.
 */
export function offerRateChipClasses(source) {
  switch (source) {
    case "ucas_eoc":
      return "border-emerald-200 bg-emerald-50 text-emerald-800";
    case "ucas_eoc_acceptance":
      return "border-sky-200 bg-sky-50 text-sky-800";
    case "course_ai_fetch":
    case "uni_offer_stats":
      return "border-stone-200 bg-stone-50 text-stone-700";
    case "legacy_editorial":
    case "legacy_editorial_stub":
    default:
      return "border-amber-200 bg-amber-50 text-amber-800";
  }
}

export function offerRateChipLabel(source) {
  switch (source) {
    case "ucas_eoc":              return "UCAS verified";
    case "ucas_eoc_acceptance":   return "UCAS EOC · acceptance";
    case "course_ai_fetch":       return "Course page";
    case "uni_offer_stats":       return "Uni-wide · verified";
    case "legacy_editorial":      return "Editorial";
    case "legacy_editorial_stub": return "Editorial estimate";
    default:                      return "";
  }
}
